import {
  Clock,
  MessageSquare,
  Stethoscope,
  Video,
} from "@tamagui/lucide-icons";
import type { ComponentProps, ReactNode } from "react";

import {
  type OneTimeProductOption,
  type PricingModel,
  type SubscriptionProductOption,
  type VisitOptionModel,
  type VisitType,
} from "@/api/generated/models";
import { Text } from "@/components";
import { formatCentsAsPriceLabel } from "@/utils/formatting";
import { PaymentMode } from "./PaymentModeToggle";

export const VisitOptionLabelMap = {
  async_rx: "async",
  async_dx: "async",
  rx_renewal: "async",
  live_chat: "live",
  live_video: "video",
  follow_up: "follow-up",
} satisfies Record<VisitType, string>;

export type OfferProductOption =
  | SubscriptionProductOption
  | OneTimeProductOption;

export type VisitOption = {
  visitType: VisitType;
  titleModifier: string;
  title: string;
  entitled: boolean;
  cashPayCents: number | null;
  defaultPriceLabel: string;
  description: string;
  details: {
    icon: ReactNode;
    description: ReactNode;
  }[];
  buttonLabel: string;
};

const iconProps: ComponentProps<typeof Clock> = {
  size: "$250",
  color: "$textPrimary",
  flexShrink: 0,
};

// All async modalities present identically (async is async); only the title differs.
const asyncVisitOption = (
  visitType: VisitType,
  title: string,
): VisitOption => ({
  visitType,
  titleModifier: "Async",
  title,
  entitled: false,
  cashPayCents: null,
  defaultPriceLabel: "$19",
  description:
    "Send your question and get a thorough response from a clinician - on your schedule.",
  details: [
    {
      icon: <Clock {...iconProps} />,
      description: (
        <Text size="$sm">
          <Text size="$sm" bold color="$textPrimary">
            Reply within 2 hrs
          </Text>
          , no waiting required
        </Text>
      ),
    },
    {
      icon: <MessageSquare {...iconProps} />,
      description: (
        <Text size="$sm">
          <Text size="$sm" bold color="$textPrimary">
            Async message
          </Text>{" "}
          - no need to be online
        </Text>
      ),
    },
    {
      icon: <Stethoscope {...iconProps} />,
      description: (
        <Text size="$sm">
          Best for{" "}
          <Text size="$sm" bold color="$textPrimary">
            non-urgent
          </Text>{" "}
          issues and follow-up questions
        </Text>
      ),
    },
  ],
  buttonLabel: "Send a message",
});

export const DEFAULT_VISIT_OPTIONS: VisitOption[] = [
  {
    visitType: "live_chat",
    titleModifier: "Live",
    title: "Chat",
    entitled: false,
    cashPayCents: null,
    defaultPriceLabel: "$59",
    description:
      "Type back and forth with a clinician in real time - like texting, but with your care team.",
    details: [
      {
        icon: <Clock {...iconProps} />,
        description: (
          <Text size="$sm">
            <Text size="$sm" bold color="$textPrimary">
              30 minute
            </Text>{" "}
            average duration
          </Text>
        ),
      },
      {
        icon: <MessageSquare {...iconProps} />,
        description: (
          <Text size="$sm">
            <Text size="$sm" bold color="$textPrimary">
              Live text
            </Text>{" "}
            with a board-certified clinician
          </Text>
        ),
      },
    ],
    buttonLabel: "Start chat visit",
  },
  {
    visitType: "live_video",
    titleModifier: "Live",
    title: "Video",
    entitled: false,
    cashPayCents: null,
    defaultPriceLabel: "$59",
    description:
      "Face-to-face with a clinician from wherever you are. No waiting rooms, no commute.",
    details: [
      {
        icon: <Clock {...iconProps} />,
        description: (
          <Text size="$sm">
            <Text size="$sm" bold color="$textPrimary">
              15 minute
            </Text>{" "}
            average duration
          </Text>
        ),
      },
      {
        icon: <Video {...iconProps} />,
        description: (
          <Text size="$sm">
            <Text size="$sm" bold color="$textPrimary">
              Live video
            </Text>{" "}
            with a board-certified clinician
          </Text>
        ),
      },
    ],
    buttonLabel: "Start video visit",
  },
  // The async modalities share one presentation; each is a distinct product (FIDO-663) keyed by
  // the clinic-request type. title distinguishes them on the card.
  asyncVisitOption("async_rx", "Prescription"),
  asyncVisitOption("async_dx", "Diagnosis"),
  asyncVisitOption("rx_renewal", "Refill"),
];

export const STATIC_OPTIONS_BY_VISIT_TYPE: Record<VisitType, VisitOption> =
  Object.fromEntries(
    DEFAULT_VISIT_OPTIONS.map((option) => [option.visitType, option]),
  ) as Record<VisitType, VisitOption>;

const getCashPayCents = (
  pricing: PricingModel[] | undefined,
): number | null => {
  const cashPayPrice = pricing?.find((price) => price.type === "cash-pay");
  return cashPayPrice ? cashPayPrice.amount : null;
};

export const getVisitOptions = (
  apiVisitOptions: VisitOptionModel[] = [],
  productOptions: OfferProductOption[] = [],
): {
  options: VisitOption[];
  partnerName?: string;
} => {
  const options: VisitOption[] = [];
  const partnerName =
    apiVisitOptions.find((option) => option.sponsorship)?.sponsorship?.name ??
    undefined;

  for (const apiVisitOption of apiVisitOptions) {
    const staticOption =
      STATIC_OPTIONS_BY_VISIT_TYPE[apiVisitOption.visit_type];
    if (!staticOption) {
      console.warn(
        `No visit option presentation found for visit type "${apiVisitOption.visit_type}"; skipping.`,
      );
      continue;
    }

    // We can expect a one-time product for each visit type, lacking this would imply we
    // have a visit type that can only be accessed via subscription. Not currently in scope.
    const oneTimeProduct = productOptions.find(
      (product) =>
        product.type === "one-time" &&
        product.visit_types?.includes(apiVisitOption.visit_type),
    );

    options.push({
      ...staticOption,
      entitled: apiVisitOption.entitlement_state.entitled,
      cashPayCents: getCashPayCents(oneTimeProduct?.pricing),
    });
  }

  return {
    options: options.length > 0 ? options : DEFAULT_VISIT_OPTIONS,
    partnerName,
  };
};

export const getPriceLabel = (
  option: VisitOption,
  mode: PaymentMode,
  patientResponsibilityCents: number | null,
): string => {
  if (option.entitled) return "Free";
  if (mode === PaymentMode.insurance && patientResponsibilityCents !== null) {
    return formatCentsAsPriceLabel(patientResponsibilityCents);
  }
  if (option.cashPayCents !== null) {
    return formatCentsAsPriceLabel(option.cashPayCents);
  }
  return option.defaultPriceLabel;
};
